'use client'

import { Note, SelectionGroup, Spacer, Text } from '@vinted/web-ui'
import { useCallback, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import {
  EscrowFees,
  getPriceEstimateWithFees,
  transformPriceEstimateWithFees,
} from '@marketplace-web/escrow-pricing/escrow-pricing-data'
import {
  clickEvent,
  OfferPriceSuggestionOption,
  OfferRequestPriceSuggestionModel,
} from '@marketplace-web/offers/price-offers-data'
import { CurrencyAmountModel, formatCurrencyAmount } from '@marketplace-web/currency/currency-data'

import { OfferFormModel } from '../types'
import { OfferPriceField } from './OfferPriceField'
import { OfferFormField } from '../constants'

const CUSTOM_OFFER_PRICE_OPTION: OfferPriceSuggestionOption = {
  isCustom: true,
}

type Props = {
  maxDiscount?: string
  transactionId?: number
  minItemPrice: CurrencyAmountModel | undefined
  maxItemPrice: CurrencyAmountModel | undefined
  currentItemPrice: CurrencyAmountModel
  offerPriceSuggestions: Array<OfferRequestPriceSuggestionModel>
  itemId: number
  orderItemIds?: Array<number>
  offerRequestSessionId?: string
  selectedOfferRequestPriceSuggestion?: OfferPriceSuggestionOption
  onSelectOfferRequestPriceSuggestion?: (offerPriceSuggestion: OfferPriceSuggestionOption) => void
}

const OfferPriceWithSuggetionsField = ({
  maxDiscount,
  minItemPrice,
  maxItemPrice,
  transactionId,
  currentItemPrice,
  offerPriceSuggestions,
  itemId,
  orderItemIds,
  offerRequestSessionId,
  selectedOfferRequestPriceSuggestion,
  onSelectOfferRequestPriceSuggestion,
}: Props) => {
  const { register, setValue } = useFormContext<OfferFormModel>()

  const { track } = useTracking()
  const translate = useTranslate('conversation.hero_message.make_offer.offer_modal')
  const { locale } = useIntl()

  const { fetch: fetchPriceEstimateWithFees, transformedData: priceEstimate } = useFetch(
    getPriceEstimateWithFees,
    transformPriceEstimateWithFees,
  )

  const getItemIds = useCallback(
    () => (orderItemIds ? orderItemIds.map(String) : [String(itemId)]),
    [orderItemIds, itemId],
  )

  useEffect(() => {
    if (!selectedOfferRequestPriceSuggestion?.suggestion?.price) return

    fetchPriceEstimateWithFees({
      item_ids: getItemIds(),
      offer_price: {
        amount: selectedOfferRequestPriceSuggestion?.suggestion?.price.amount,
        currency_code: selectedOfferRequestPriceSuggestion?.suggestion?.price.currencyCode,
      },
      fees: [EscrowFees.BuyerProtection],
    })
  }, [
    fetchPriceEstimateWithFees,
    getItemIds,
    selectedOfferRequestPriceSuggestion?.suggestion?.price,
  ])

  const offerPriceOptions = useMemo<Array<OfferPriceSuggestionOption>>(() => {
    const optionsFromSuggestions = offerPriceSuggestions.map(suggestion => ({
      suggestion,
      isCustom: false,
    }))

    return [...optionsFromSuggestions, CUSTOM_OFFER_PRICE_OPTION]
  }, [offerPriceSuggestions])

  useEffect(() => {
    if (!selectedOfferRequestPriceSuggestion) return

    register(OfferFormField.Offer)
    setValue(
      OfferFormField.Offer,
      selectedOfferRequestPriceSuggestion.suggestion?.price.amount || '',
    )
  }, [register, setValue, selectedOfferRequestPriceSuggestion])

  function isSelectedOption(option: OfferPriceSuggestionOption) {
    if (selectedOfferRequestPriceSuggestion?.isCustom) return option.isCustom
    if (!option.suggestion || !selectedOfferRequestPriceSuggestion?.suggestion) return false

    return (
      option.suggestion.price.amount ===
      selectedOfferRequestPriceSuggestion?.suggestion.price.amount
    )
  }

  function getFormattedOfferPriceOptionPrice(offerPriceOption: OfferPriceSuggestionOption) {
    if (!offerPriceOption.suggestion) return ''

    return formatCurrencyAmount(offerPriceOption.suggestion.price, locale)
  }

  function handleOfferPriceOptionClick(
    offerPriceSuggestionOption: OfferPriceSuggestionOption,
    optionIndex: number,
  ) {
    return () => {
      const targetDetails = JSON.stringify({
        transaction_id: transactionId,
        offer_request_session_id: offerRequestSessionId,
        button_number: optionIndex + 1,
        price: offerPriceSuggestionOption.suggestion?.price.amount || null,
        current_price: currentItemPrice.amount,
      })

      track(
        clickEvent({
          screen: 'buyer_originating_offer',
          target: 'select_suggested_discount',
          targetDetails,
        }),
      )

      onSelectOfferRequestPriceSuggestion?.(offerPriceSuggestionOption)
    }
  }

  const getTotalCombinedPriceText = () => (
    <Text
      as="h4"
      testId="total-combined-price"
      type="caption"
      text={
        priceEstimate &&
        translate('total_combined_price', {
          price: formatCurrencyAmount(
            {
              amount: priceEstimate.totalPrice.amount,
              currencyCode: priceEstimate.totalPrice.currencyCode,
            },
            locale,
          ),
        })
      }
    />
  )

  const getOptionTitle = (option: OfferPriceSuggestionOption) => {
    return option.isCustom
      ? translate('price_suggestion_selector.custom_price_option.title')
      : getFormattedOfferPriceOptionPrice(option)
  }

  const getOptionBody = (option: OfferPriceSuggestionOption) => {
    return option.isCustom
      ? translate('price_suggestion_selector.custom_price_option.action')
      : option.suggestion?.label
  }

  const getOptionWidth = () => {
    const optionWidth = '33%'
    const gapOffset = '6px'
    const borderOffset = '1px'

    return `calc(${optionWidth} - ${gapOffset} - ${borderOffset})`
  }

  return (
    <>
      <Spacer />
      <div className="u-ui-margin-horizontal-large" data-testid="price-suggestions">
        <SelectionGroup direction="horizontal" styling="tight" layout="default">
          {offerPriceOptions.map((option, index) => (
            <SelectionGroup.SelectionItem
              key={[option.isCustom, option.suggestion?.label].join('-')}
              title={getOptionTitle(option)}
              body={<Text text={getOptionBody(option)} theme="primary" type="caption" as="span" />}
              isSelected={isSelectedOption(option)}
              showSelectionIndicator={false}
              size={SelectionGroup.SelectionItem.Size.Small}
              onClick={handleOfferPriceOptionClick(option, index)}
              width={getOptionWidth()}
              testId={option.isCustom ? 'custom-offer-price-option' : 'offer-price-option'}
            />
          ))}
        </SelectionGroup>
      </div>
      {selectedOfferRequestPriceSuggestion?.isCustom ? (
        <OfferPriceField
          showTitle={false}
          maxDiscount={maxDiscount}
          minItemPrice={minItemPrice}
          maxItemPrice={maxItemPrice}
          currentItemPrice={currentItemPrice}
          itemId={itemId}
          orderItemIds={orderItemIds}
        />
      ) : (
        <Note text={getTotalCombinedPriceText()} />
      )}
    </>
  )
}

export default OfferPriceWithSuggetionsField
